import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { searchScrape as searchScrapeApi, TSearchScrapeParams, TSearchScrapeItem } from '@remote/api/adapter/shell-mqtt'
import fallback from '@shared/assets/img/cinema-fallback.png'
import { parseTime, formatSize as bytesToSize } from '@remote/utils'
import { VideoFileIcon } from '@remote/utils/file-helper'
import global from '@remote/store/constantStore'
import { safeJSONParse } from '@shared/utils'
import { checkNetworkState, checkDeviceState, checkDiskState } from '@remote/store/utils'
import { escapeRegex } from '@remote/utils/file-helper'
export interface TSearchResourceRetItem {
  class: string
  id: string
  hash:string
  file_size:string
  file_path: string
  name: string
  actors: string[]
  directors: string[]
  brief: string
  postUrl: string
  year: string
  originPlace: string
  displayClass:string
  displayEpisode:string
  displayName:string
  isOther:boolean
  isTV:boolean
  scrape_info:TSearchScrapeItem['scrape_info']
}

interface ICinemaSearchStore {
  phase: string
  prevPhase: string
  map: { [id: string]: TSearchResourceRetItem }
  class: 'all' | 'tv' | 'movie' | 'other'
  filesIds: {
    all: string[]
    tv: string[]
    movie: string[]
    other: string[]
  }
  filesNums: {
    all: number
    tv: number
    movie: number
    other: number
  }
  hasMore: {
    all: boolean
    tv: boolean
    movie: boolean
    other: boolean
  }
  loading: {
    all: boolean
    tv: boolean
    movie: boolean
    other: boolean
  }
}

const classMap = {
  all: '',
  tv: 'tv',
  movie: 'movie',
  other: 'others',
}

const filesIds = {
  all: [],
  tv: [],
  movie: [],
  other: [],
}

const filesNums = {
  all: 0,
  tv: 0,
  movie: 0,
  other: 0,
}

const hasMore = {
  all: true,
  tv: true,
  movie: true,
  other: true,
}

const loading = {
  all: false,
  tv: false,
  movie: false,
  other: false,
}

const defaultLastPageToken = {
  all: '',
  tv: '',
  movie: '',
  other: '',
}

const pageSize = 50

function reduceFilename (drama:API_NFO.Drama) {
  if (!drama) return ''
  // const name = drama.short_name || drama.trans_name || drama.name
  const season = drama.season ? +drama.season : 0
  const episode = drama.episode ? +drama.episode : 0
  const nfoName = [
    season ? `第${season}季` : '',
    episode ? `第${episode}集` : '',
  ].filter(label => !!label).join('·')
  const fileName = drama.file_name || ''
  if (episode) return nfoName
  return fileName
}

const displayClassMap = {
  tv: '电视剧',
  // movie: '电影',
}

const formatSearchResourceRetItem = (item:TSearchScrapeItem, regex:RegExp):TSearchResourceRetItem => {
  const isOther = !item.scrape_info.class
  const name = item.scrape_info.trans_name || item.scrape_info.name || item.scrape_info.title || item.file_name
  const displayName = name?.replace(regex, function (match:string) {
    return `<label>${match}</label>`
  })

  return {
    id: item.md5sum,
    hash: item.hash,
    file_size: item.file_size,
    file_path: item.file_path,
    class: item.scrape_info.class,
    name: name,
    actors: item.scrape_info?.actors?.map(actor => actor.name),
    directors: item.scrape_info?.directors?.map(director => director.name),
    brief: item.scrape_info.brief,
    postUrl: isOther ? VideoFileIcon : item.scrape_info?.post_url?.length ? item.scrape_info?.post_url[0] : fallback,
    year: item.scrape_info.year,
    originPlace: item.scrape_info.origin_place,
    displayClass:displayClassMap[item.scrape_info.class] || parseTime(item.file_ut),
    displayEpisode: isOther ? bytesToSize(item.file_size) : reduceFilename(item.scrape_info),
    displayName,
    isOther,
    isTV: item.scrape_info.class === 'tv',
    scrape_info: item.scrape_info,
  }
}

export const useCinemaSearch = defineStore('cinemaSearch', () => {

  let lastPageToken = {
    all: '',
    tv: '',
    movie: '',
    other: '',
  }

  const data = reactive<ICinemaSearchStore>({
    phase: '',
    prevPhase: '',
    map: {},
    class: 'all',
    filesIds,
    filesNums,
    hasMore,
    loading,
  })

  const initData = () => {
    data.filesIds = Object.create(filesIds)
    data.filesNums = Object.create(filesNums)
    data.hasMore = Object.create(hasMore)
    data.loading = Object.create(loading)
    data.map = {}
    lastPageToken = Object.create(defaultLastPageToken)
    console.log('initData', lastPageToken)
  }

  const abnormal = ref({type: '', errorMessage: ''})

  const searchScrape = (searchClass?: ICinemaSearchStore['class'], needInit?:boolean) => {
    if (needInit) initData()

    const networkAbnormalMessage = checkNetworkState()
    if (networkAbnormalMessage) {
      abnormal.value.type = 'offline'
      abnormal.value.errorMessage = networkAbnormalMessage
      return 
    }
    const deviceAbnormalMessage = checkDeviceState()
    if (deviceAbnormalMessage) {
      abnormal.value.type = 'device'
      abnormal.value.errorMessage = deviceAbnormalMessage
      return 
    }
    const diskAbnormalMessage = checkDiskState()
    if (diskAbnormalMessage) {
      abnormal.value.type = 'disk'
      abnormal.value.errorMessage = diskAbnormalMessage
      return 
    }

    abnormal.value.type = ''
    abnormal.value.errorMessage = ''

    const curClass = searchClass || data.class
    const curLastPageToken = lastPageToken[curClass]
    const params = {
      phase: data.phase,
      class: classMap[curClass],
      page_size: pageSize,
    } as TSearchScrapeParams
    console.log('curLastPageToken', lastPageToken, curLastPageToken)
    if (curLastPageToken) params.last_page_token = curLastPageToken

    data.loading[curClass] = true

    searchScrapeApi(params)
      .then((ret) => {
        const [res, searchPhase] = ret
        if (searchPhase !== data.phase) return
        const { last_page_token, file_nums, result } = res
        const hasMore = !!last_page_token && file_nums >= pageSize && last_page_token !== curLastPageToken

        data.filesNums[curClass] = file_nums
        lastPageToken[curClass] = hasMore ? last_page_token : ''
        data.hasMore[curClass] = hasMore

        const nextList: string[] = []
        const regex = new RegExp(escapeRegex(params.phase), 'ig')
        result?.forEach(item => {
          data.map[item.md5sum] = formatSearchResourceRetItem(item, regex)
          nextList.push(item.md5sum)
        })

        data.filesIds[curClass] = curLastPageToken ? [...data.filesIds[curClass], ...nextList] : nextList
      })
      .catch(err => {
        console.log(err)
        abnormal.value.type = 'api-error'
        abnormal.value.errorMessage = err
      })
      .finally(() => {
        data.loading[curClass] = false
      })
  }

  const setSearchPhase = (phase: string, searchClass?: ICinemaSearchStore['class']) => {
    data.phase = phase
    initData()
    searchScrape(searchClass)
  }

  const setSearchClass = (searchClass: ICinemaSearchStore['class']) => {
    if (data.class !== searchClass) {
      data.class = searchClass
      searchScrape()
    }
  }

  const cleanSearchPhase = () => {
    data.phase = ''
    data[data.class] = []
    initData()
  }

  return {
    data,
    setSearchPhase,
    searchScrape,
    cleanSearchPhase,
    setSearchClass,
    abnormal,
  }
})

const CS_HISTORYS_LOCAL_CACHE_KEY = `CS_HISTORYS_LOCAL_CACHE_KEY_${global.userId}`

let historys:string[] = []
export const getHistoryLocalCache = (): string[] => {
  const cache = localStorage.getItem(CS_HISTORYS_LOCAL_CACHE_KEY)
  if (cache) {
    const cacheHistorys = safeJSONParse(cache)
    historys = (Array.isArray(cacheHistorys) ? cacheHistorys : []).slice(0, 50)
    return historys
  } else {
    return []
  }
}

export const setHistoryLocalCache = (data: string[]) => {
  historys = data.slice()
  localStorage.setItem(CS_HISTORYS_LOCAL_CACHE_KEY, JSON.stringify(data))
}

export const addHistoryLocalCache = (key:string) => {
  if (!key) return
  const index = historys.findIndex(item => item === key)
  if (index > - 1) historys.splice(index, 1)
  setHistoryLocalCache([key, ...historys])
  return historys
}

export const removeHistoryLocalCache = (key:string): string[] => {
  if (!key) return historys
  const index = historys.findIndex(item => item === key)
  if (index > - 1) historys.splice(index, 1)
  setHistoryLocalCache([...historys])
  return historys
}

export const removeAllHistoryLocalCache = (): string[] => {
  setHistoryLocalCache([])
  return historys
}